import URLS from "~/constants/urls"
import { ITokenInfo } from "~/types/global"
import { axiosInstance, retryFetch } from "~/utils/axios"
import { toPlainStringForApi } from "~/utils/location-helpers"
import { parseSseStream } from "~/utils/sse-parser"

export async function getPreMeetingAccountDetails(
  tokenInfo: ITokenInfo,
  website: any,
  company_id: any,
  session_id: string,
  name: string,
  multipleData?: any[]
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/pre_meeting/generate_account_brief`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      website: website,
      company_id: company_id,
      session_id: session_id,
      name,
      multipleData
    }
  })
}

export async function getHistory(tokenInfo: ITokenInfo) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/pre_meetings`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getUpcomingMeetingBriefs(tokenInfo: ITokenInfo) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/up_coming_meetings_brief`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getPersonBriefBulk(
  tokenInfo: ITokenInfo,
  users: any,
  session_id: any
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/pre_meeting/generate_person_brief_bulk`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      users,
      session_id: session_id
    }
  })
}

export async function peopleSearch(
  tokenInfo: ITokenInfo | null,
  name: string,
  company_id: any,
  person_seniorities: string[],
  person_locations: string[]
) {
  const sanitizedName = name.replace(/\/+$/, "")
  const lowercaseSeniorities = person_seniorities.map((seniority) =>
    seniority.toLowerCase()
  )

  return await retryFetch(
    `${URLS.V1}/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/pre_meeting/peopleSearch`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${tokenInfo?.token}`
      },
      body: JSON.stringify({
        name: sanitizedName,
        company_id,
        pageNo: 1,
        person_seniorities: lowercaseSeniorities,
        person_locations
      })
    }
  )
}

export async function searchCompanies(
  tokenInfo: ITokenInfo | null,
  inputValue: string
) {
  return await retryFetch(
    `${URLS.V1}/tenants/${tokenInfo?.tenant_id}/users/${
      tokenInfo?.user_id
    }/search_companies?search=${encodeURIComponent(inputValue)}`,
    {
      method: "GET",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${tokenInfo?.token}`
      }
    }
  )
}

export const downloadPreMeeting = async (
  tokenInfo: ITokenInfo,
  meetingId: string
) => {
  const timestamp = new Date().getTime()
  return await axiosInstance({
    method: "GET",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/pre_meeting/${meetingId}/download?session_id=${timestamp}`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    responseType: "blob"
  })
}

export async function sharePreMeetingViaEmail(
  tokenInfo: ITokenInfo | null,
  emails: string[],
  meetingId: string
) {
  return await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/pre_meeting/${meetingId}/share_via_email`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      emails: emails
    }
  })
}

export async function getSinglePersonBrief(
  tokenInfo: ITokenInfo,
  linkedin_url: any,
  person_name: any,
  account_name: any,
  accountId: string | number,
  session_id: any,
  // Optional DB account id. When the apollo org id (accountId) is empty, the
  // backend resolves apollo_id from this via getAccountById — so a person with
  // no apollo_id but a known account still gets a real brief.
  accountDbId?: string | number | null
) {
  const users = {
    [linkedin_url]: {
      name: toPlainStringForApi(person_name),
      organization_id: accountId ? String(accountId) : "",
      ...(accountDbId ? { account_id: String(accountDbId) } : {}),
      organization_name: toPlainStringForApi(account_name)
    }
  }
  return await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/pre_meeting/generate_person_brief_bulk`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      session_id: session_id,
      users
    }
  })
}

export async function getSinglePersonBriefStream(
  tokenInfo: ITokenInfo,
  linkedin_url: string,
  person_name: string,
  account_name: string,
  accountId: string | number,
  session_id: string,
  onFrame: (event: string, data: any) => void,
  accountDbId?: string | number | null,
  signal?: AbortSignal,
  // Hard client-side deadline. The raw fetch (unlike axiosInstance) has no
  // timeout, and the backend keeps the socket alive with heartbeats, so a stuck
  // generation would otherwise hang forever. On deadline we abort with a
  // TimeoutError (distinct from the caller's user-close AbortError) so callers
  // can surface it as a failure rather than swallow it.
  deadlineMs = 120000
): Promise<void> {
  const users = {
    [linkedin_url]: {
      name: toPlainStringForApi(person_name),
      organization_id: accountId ? String(accountId) : "",
      ...(accountDbId ? { account_id: String(accountDbId) } : {}),
      organization_name: toPlainStringForApi(account_name)
    }
  }
  const ctrl = new AbortController()
  const onCallerAbort = () => ctrl.abort(signal?.reason)
  if (signal) {
    if (signal.aborted) ctrl.abort(signal.reason)
    else signal.addEventListener("abort", onCallerAbort, { once: true })
  }
  const timer = setTimeout(
    () =>
      ctrl.abort(
        new DOMException(
          "person brief stream deadline exceeded",
          "TimeoutError"
        )
      ),
    deadlineMs
  )
  try {
    const res = await fetch(
      `${URLS.V1}/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/pre_meeting/generate_person_brief_bulk_stream?stream=1`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${tokenInfo?.token}`,
          "Content-Type": "application/json",
          Accept: "text/event-stream"
        },
        body: JSON.stringify({ session_id, users }),
        signal: ctrl.signal
      }
    )
    if (!res.ok || !res.body)
      throw new Error(`stream failed: ${res.status} ${res.statusText}`)
    for await (const frame of parseSseStream(res.body)) {
      onFrame(frame.event, frame.data)
    }
  } finally {
    clearTimeout(timer)
    if (signal) signal.removeEventListener("abort", onCallerAbort)
  }
}

export const downloadPersonBrief = async (
  tokenInfo: ITokenInfo,
  linkedin_url: string,
  // Already-generated PMB sections from the client (React Query cache). We pass
  // them through so the PDF renders exactly what's on screen, no regeneration.
  sections?: unknown[]
) => {
  return await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/pre_meeting/download_person_brief`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      linkedin_url,
      sections: sections ?? []
    },
    responseType: "blob"
  })
}
